import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Crown, Users, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEntitlement, SUBSCRIPTION_TIERS } from '@/contexts/EntitlementContext';

/**
 * ProtectedFeaturePage - Full page wrapper for features that require a subscription
 * 
 * Use this to wrap entire pages that should be gated.
 * Shows a beautiful upgrade prompt page instead of the content.
 * 
 * Usage:
 * <ProtectedFeaturePage feature="doctor_summary" title="Doctor Summary">
 *   <DoctorSummaryContent />
 * </ProtectedFeaturePage>
 */
export function ProtectedFeaturePage({ 
  feature, 
  title,
  description,
  children,
  backPath = '/emergency-binder',
}) {
  const navigate = useNavigate();
  const { checkFeatureAccess, getTierInfo, isDemoMode } = useEntitlement();

  // Demo mode always shows content
  if (isDemoMode) {
    return children;
  }

  const access = checkFeatureAccess(feature);

  // Feature is allowed - show content
  if (access.allowed) {
    return children;
  }

  // Feature is locked - show upgrade page
  const requiredTier = access.requiredTier || 'premium';
  const tierInfo = getTierInfo(requiredTier);
  const featureInfo = access.feature || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-lg font-semibold text-slate-800">{title || featureInfo.name}</h1>
        </div>
      </div>

      {/* Locked content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto px-4 py-6 pb-28"
      >
        <div className="text-center">
          {/* Lock icon with tier color */}
          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
            requiredTier === SUBSCRIPTION_TIERS.FAMILY 
              ? 'bg-purple-100' 
              : 'bg-teal-100'
          }`}>
            <Lock className={`w-8 h-8 ${
              requiredTier === SUBSCRIPTION_TIERS.FAMILY 
                ? 'text-purple-500' 
                : 'text-teal-500'
            }`} />
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            {title || featureInfo.name}
          </h2>

          {/* Description */}
          <p className="text-sm text-slate-600 mb-2">
            {description || access.upgradePrompt || featureInfo.description}
          </p>

          {/* Tier badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 ${
            requiredTier === SUBSCRIPTION_TIERS.FAMILY 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-teal-100 text-teal-700'
          }`}>
            {requiredTier === SUBSCRIPTION_TIERS.FAMILY ? (
              <Users className="w-4 h-4" />
            ) : (
              <Crown className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {tierInfo.name} Feature
            </span>
          </div>

          {/* Benefits */}
          <div className={`p-5 rounded-2xl mb-5 ${
            requiredTier === SUBSCRIPTION_TIERS.FAMILY 
              ? 'bg-purple-50 border border-purple-100' 
              : 'bg-teal-50 border border-teal-100'
          }`}>
            <h3 className="font-semibold text-slate-800 mb-3 text-sm">
              What you get with {tierInfo.name}:
            </h3>
            <ul className="text-left space-y-2 text-sm text-slate-600">
              {requiredTier === SUBSCRIPTION_TIERS.FAMILY ? (
                <>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Up to 10 family profiles
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Network & sharing
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Caregiver access
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Emergency sharing with QR codes
                  </li>
                </>
              ) : feature === 'vehicle_records' ? (
                <>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Multiple vehicle profiles
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Maintenance schedules & reminders
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Service history & receipt storage
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Vehicle records & VIN lookup
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Exportable vehicle summary
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Unlimited medical records
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Document storage & scanning
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Doctor Summary & PDF exports
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-teal-500" />
                    Vehicle records & VIN lookup
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/subscription/plans', { 
                state: { 
                  requiredTier, 
                  featureKey: feature,
                  returnPath: window.location.pathname 
                } 
              })}
              size="lg"
              className={`w-full ${
                requiredTier === SUBSCRIPTION_TIERS.FAMILY
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Upgrade to {tierInfo.name} - {tierInfo.priceDisplay}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(backPath)}
              className="w-full text-slate-500"
            >
              Maybe Later
            </Button>
          </div>

          {/* Reassurance */}
          <p className="text-xs text-slate-400 mt-6">
            Cancel anytime. Your data is always safe.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * useFeatureAccess - Hook for checking feature access in components
 * 
 * Usage:
 * const { canAccess, showUpgrade } = useFeatureAccess('doctor_summary');
 * if (!canAccess) {
 *   showUpgrade();
 *   return;
 * }
 */
export function useFeatureAccess(featureKey) {
  const navigate = useNavigate();
  const { checkFeatureAccess, isDemoMode } = useEntitlement();

  const access = checkFeatureAccess(featureKey);
  const canAccess = isDemoMode || access.allowed;

  const showUpgrade = () => {
    navigate('/subscription/plans', {
      state: {
        requiredTier: access.requiredTier,
        featureKey,
        returnPath: window.location.pathname,
      },
    });
  };

  return {
    canAccess,
    access,
    showUpgrade,
    requiredTier: access.requiredTier,
    upgradePrompt: access.upgradePrompt,
  };
}

export default ProtectedFeaturePage;
